import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { ChevronRight, Home } from "lucide-react";

/**
 * Breadcrumbs component - Shows current navigation path
 * Provides context to users about their location in the app
 */
const BreadcrumbsBase = ({ category, location }) => {

  // Map of paths to readable labels
  const pathLabels = useMemo(() => ({
    // Acciones
    '/hotelrental': 'Montarto',
    '/lockersale': 'Vender',
    '/pin': 'Recuperar PIN',
    '/supportticketadd': 'Nuevo Ticket',
    
    // Canales
    '/machines': category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos',
    '/qrs': 'QRs',
    '/pdas': 'PDAs',
    '/operators': 'Agregadores',
    
    // Gestión
    '/rentals': 'Reservas',
    '/calendars': 'Calendarios',
    '/redeemcodes': 'Redeem Codes',
    '/email-templates': 'Plantillas de Email',
    '/email-template': 'Plantilla de Email',
    '/partners': 'Partners',
    '/discount-codes': 'Códigos de Descuento',
    
    // Reporte
    '/operational-dashboard': 'Dashboard Operativo',
    '/dashboard-rental': 'Dashboard Alquileres',
    '/sales': 'Ventas',
    '/internalsales': 'Análisis por tipo',
    '/arqueos': 'Gráficos',
    '/arqueossales': 'KPI',
    '/withdrawals': 'Retiros',
    '/ocupation': 'Ocupación',
    '/objectives': 'Objetivos',
    '/localizacion': 'Localización',
    '/occupancy-dashboard': 'Dashboard Ocupación',
    '/ocupationaccumulated': 'Ocupación acumulada',
    '/ocupationdaily': 'Ocupación diaria',
    
    // Operativa
    '/supporttickets': 'Tickets',
    '/faults': 'Averías',
    '/incidences': 'Incidencias',
    '/slas': 'SLA',
    '/audit': 'Audit',
    '/tpvs': 'TPVs',
    '/modules': 'Módulos',
    '/matrixs': 'Versiones',
    '/lowbluetooth': 'Bluetooth',
    '/lockersqty': 'Lockers QTY',
    '/openings': 'Aperturas',
    '/pinrecovers': 'Recuperación PINes',
    '/rules': 'Reglas de negocio',
    '/articles': 'Artículos',
    '/arqueosbaqueira': 'Arqueos ERP Baqueira',
    
    // Soporte
    '/support-chat': 'Chat',
    '/faqs': 'FAQs',
    '/manuals': 'Manuales',
    '/integraciones': 'Integraciones',
    '/api': 'API',
    '/planes': 'Planes',
    
    // Usuarios
    '/my-cash-users': 'Usuarios Cash-Control',
    '/my-pda-users': 'Usuarios Operativo',
    
    // Admin
    '/customers': 'Clientes',
    '/contracts': 'Contratos',
    '/gameranking': 'Juegos',
    '/replacements': 'Recambios',
    '/users': 'Administradores',
  }), [category]);

  // Map of paths to their parent categories
  const pathCategories = useMemo(() => ({
    // Acciones
    '/hotelrental': 'Acciones',
    '/lockersale': 'Acciones',
    '/pin': 'Acciones',
    '/supportticketadd': 'Acciones',
    
    // Canales
    '/machines': 'Canales',
    '/qrs': 'Canales',
    '/pdas': 'Canales',
    
    // Gestión
    '/rentals': 'Gestión',
    '/calendars': 'Gestión',
    '/redeemcodes': 'Gestión',
    '/email-templates': 'Gestión',
    '/email-template': 'Gestión',
    '/partners': 'Gestión',
    '/discount-codes': 'Gestión',
    
    // Reporte
    '/operational-dashboard': 'Reporte',
    '/dashboard-rental': 'Reporte',
    '/sales': 'Reporte',
    '/internalsales': 'Reporte',
    '/arqueos': 'Reporte',
    '/arqueossales': 'Reporte',
    '/withdrawals': 'Reporte',
    '/ocupation': 'Reporte',
    '/objectives': 'Reporte',
    '/localizacion': 'Reporte',
    '/occupancy-dashboard': 'Reporte',
    '/ocupationaccumulated': 'Baqueira',
    '/ocupationdaily': 'Baqueira',
    
    // Operativa
    '/supporttickets': 'Operativa',
    '/faults': 'Operativa',
    '/incidences': 'Operativa',
    '/slas': 'SAT',
    '/audit': 'Operativa',
    '/tpvs': 'Operativa',
    '/modules': 'Operativa',
    '/matrixs': 'Operativa',
    '/lowbluetooth': 'Operativa',
    '/lockersqty': 'Operativa',
    '/openings': 'Operativa',
    '/pinrecovers': 'Reporte',
    
    // Baqueira
    '/operators': 'Baqueira',
    '/rules': 'Baqueira',
    '/articles': 'Baqueira',
    '/arqueosbaqueira': 'Baqueira',
    
    // Soporte
    '/support-chat': 'Soporte',
    '/faqs': 'Soporte',
    '/manuals': 'Soporte',
    '/integraciones': 'Soporte',
    '/api': 'Reporte',
    '/planes': 'Soporte',
    
    // Usuarios
    '/my-cash-users': 'Usuarios',
    '/my-pda-users': 'Usuarios',
    
    // Admin
    '/customers': 'Administración',
    '/contracts': 'Administración',
    '/gameranking': 'Administración',
    '/replacements': 'Administración',
    '/users': 'Administración',
  }), []);

  // Build breadcrumb items from current path
  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname;
    const items = [];

    // Always add home
    items.push({
      label: 'Inicio',
      path: '/',
      icon: Home,
    });

    // Check if we're on home page
    if (pathname === '/') {
      return items;
    }

    // Get base path (without ID parameters)
    const pathParts = pathname.split('/').filter(Boolean);
    const basePath = `/${pathParts[0]}`;
    
    // Add category if exists
    const categoryName = pathCategories[basePath];
    if (categoryName) {
      items.push({
        label: categoryName,
        path: null, // Categories are not clickable
        icon: null,
      });
    }

    // Add current page
    const pageLabel = pathLabels[basePath];
    if (pageLabel) {
      items.push({
        label: pageLabel,
        path: basePath,
        icon: null,
      });
    }

    // If there's an ID in the URL (like /machines/123)
    if (pathParts.length > 1) {
      const id = pathParts[pathParts.length - 1];
      // Only show if it looks like an ID (not another route segment)
      if (id.length > 3 && !/^[a-z]+$/.test(id)) {
        items.push({
          label: `Detalle`,
          path: pathname,
          icon: null,
        });
      }
    }

    return items;
  }, [location.pathname, pathLabels, pathCategories]);

  // Don't render if we only have home (we're on home page)
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const Icon = crumb.icon;

          return (
            <li key={index} className="breadcrumbs-item">
              {crumb.path && !isLast ? (
                <Link 
                  to={crumb.path} 
                  className="breadcrumbs-link"
                  aria-label={`Ir a ${crumb.label}`}
                >
                  {Icon && <Icon size={14} className="breadcrumbs-icon" />}
                  <span>{crumb.label}</span>
                </Link>
              ) : (
                <span 
                  className={`breadcrumbs-text ${isLast ? 'breadcrumbs-current' : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {Icon && <Icon size={14} className="breadcrumbs-icon" />}
                  <span>{crumb.label}</span>
                </span>
              )}
              {!isLast && (
                <ChevronRight 
                  size={14} 
                  className="breadcrumbs-separator"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const mapStateToProps = (state) => ({
  location: state.router ? state.router.location : { pathname: '/' },
});

const Breadcrumbs = connect(mapStateToProps)(BreadcrumbsBase);

export default Breadcrumbs;
